import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { Trophy, Plus, TrendingUp, Clock, Search, FileText, Target, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import CompetitionModal from '../components/CompetitionModal';
import CompetitionCard from '../components/CompetitionCard';
import ApplicationModal from '../components/ApplicationModal';
import CompetitionMetrics from '../components/CompetitionMetrics';
import { competitionService } from '../api/competitionsApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ─── Shared primitives ────────────────────────────────────────────────────────

const Spinner = () => (
  <div className="flex items-center justify-center h-64">
    <div
      className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
      style={{ borderColor: BRAND_COLORS.primary, borderTopColor: 'transparent' }}
    />
  </div>
);

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div
    className="flex flex-col items-center justify-center py-16 px-6 text-center rounded-2xl border"
    style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
  >
    <div
      className="flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
    >
      <Icon className="w-7 h-7" style={{ color: BRAND_COLORS.primary }} />
    </div>
    <p className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{title}</p>
    <p className="mt-1 text-sm max-w-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{subtitle}</p>
  </div>
);

const appStatusStyle = (status) => {
  switch (status) {
    case 'awarded':      return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    case 'submitted':    return { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info };
    case 'under_review': return { backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
    case 'rejected':     return { backgroundColor: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger };
    default:             return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  }
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',      label: 'Overview',      icon: Target },
  { id: 'competitions',  label: 'Competitions',  icon: Trophy },
  { id: 'applications',  label: 'Applications',  icon: FileText },
];

const Competitions = () => {
  const { hasPermission } = useApiAuth();

  const [competitions, setCompetitions]       = useState([]);
  const [applications, setApplications]       = useState([]);
  const [metrics, setMetrics]                 = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [searchTerm, setSearchTerm]           = useState('');
  const [statusFilter, setStatusFilter]       = useState('all');
  const [showCompetitionModal, setShowCompetitionModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition]   = useState(null);
  const [selectedApplication, setSelectedApplication]   = useState(null);
  const [activeTab, setActiveTab]             = useState('overview');

  const canView   = hasPermission('competitions.view');
  const canCreate = hasPermission('competitions.create');
  const canEdit   = hasPermission('competitions.edit');
  const canManage = hasPermission('competitions.manageApplications');

  useEffect(() => {
    if (canView) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [canView]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [comp, apps, met] = await Promise.all([
        competitionService.getAllCompetitions(),
        competitionService.getAllApplications(),
        competitionService.getCompetitionMetrics(),
      ]);
      setCompetitions(comp || []);
      setApplications(apps || []);
      setMetrics(met || { totalApplications: 0, successRate: 0, totalFundingReceived: 0, activeCompetitions: 0, upcomingDeadlines: 0 });
    } catch {
      toast.error('Failed to load competitions data');
      setCompetitions([]);
      setApplications([]);
      setMetrics({ totalApplications: 0, successRate: 0, totalFundingReceived: 0, activeCompetitions: 0, upcomingDeadlines: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowCompetitionModal(false);
    setShowApplicationModal(false);
    setSelectedCompetition(null);
    setSelectedApplication(null);
    loadData();
  };

  const filteredCompetitions = competitions.filter(c => {
    const q = searchTerm.toLowerCase();
    const matchSearch = c.name?.toLowerCase().includes(q) || c.organization?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  if (loading) return (
    <div className="min-h-screen">
      <Spinner />
    </div>
  );

  if (!canView) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Page header ── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.18em]"
              style={{ color: BRAND_COLORS.primary }}
            >
              Funding & Grants
            </p>
            <h1
              className="mt-1 text-2xl font-bold sm:text-3xl"
              style={{ color: NEUTRAL_COLORS.black }}
            >
              Competitions
            </h1>
            <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
              Manage grant applications and funding opportunities
            </p>
          </div>
          {canCreate && (
            <button
              onClick={() => { setSelectedCompetition(null); setShowCompetitionModal(true); }}
              className="self-start sm:self-auto flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
            >
              <Plus size={18} />
              New Competition
            </button>
          )}
        </div>

        {/* ── Metrics ── */}
        {metrics && <CompetitionMetrics metrics={metrics} />}

        {/* ── Tabs ── */}
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}
        >
          {/* Tab bar */}
          <div
            className="flex overflow-x-auto border-b"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors"
                  style={{
                    borderBottomColor: active ? BRAND_COLORS.primary : 'transparent',
                    color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                    backgroundColor: active ? SYSTEM_COLORS.shellSurfaceMuted : 'transparent',
                  }}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ── Tab: Overview ── */}
          {activeTab === 'overview' && (
            <div className="p-5 sm:p-6 space-y-6">
              {/* KPI strip */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { label: 'Active Competitions', value: metrics?.activeCompetitions ?? 0, icon: Trophy,    color: BRAND_COLORS.primary },
                  { label: 'Total Applications',  value: metrics?.totalApplications  ?? 0, icon: FileText,  color: SYSTEM_COLORS.success },
                  { label: 'Success Rate',         value: `${(metrics?.successRate ?? 0).toFixed(1)}%`, icon: TrendingUp, color: '#7c3aed' },
                  { label: 'Upcoming Deadlines',   value: metrics?.upcomingDeadlines  ?? 0, icon: Clock,     color: SYSTEM_COLORS.danger },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 p-4 rounded-xl border"
                    style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
                  >
                    <div
                      className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl"
                      style={{ backgroundColor: `${color}18` }}
                    >
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs leading-4" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
                      <p className="mt-0.5 text-xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent activity */}
              <div>
                <p
                  className="text-xs font-semibold uppercase tracking-[0.18em] mb-3"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  Recent Activity
                </p>
                {applications.length === 0 ? (
                  <EmptyState
                    icon={FileText}
                    title="No recent activity"
                    subtitle="Applications will appear here once teams start applying."
                  />
                ) : (
                  <div className="space-y-2">
                    {applications.slice(0, 5).map(app => {
                      const comp = competitions.find(c => c.id === app.competitionId);
                      return (
                        <div
                          key={app.id}
                          className="flex items-center justify-between gap-4 p-4 rounded-xl border"
                          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg"
                              style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
                            >
                              <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate" style={{ color: NEUTRAL_COLORS.black }}>
                                {comp?.name ?? 'Unknown Competition'}
                              </p>
                              <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                                {app.createdAt ? format(new Date(app.createdAt), 'MMM d, yyyy') : 'Date unavailable'}
                              </p>
                            </div>
                          </div>
                          <span
                            className="flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full capitalize"
                            style={appStatusStyle(app.status)}
                          >
                            {app.status.replace('_', ' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Tab: Competitions ── */}
          {activeTab === 'competitions' && (
            <div className="p-5 sm:p-6 space-y-5">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    style={{ color: SYSTEM_COLORS.textSoft }}
                  />
                  <input
                    type="text"
                    placeholder="Search competitions…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none transition-all"
                    style={{
                      borderColor: SYSTEM_COLORS.border,
                      backgroundColor: SYSTEM_COLORS.shellSurface,
                      color: NEUTRAL_COLORS.black,
                    }}
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="px-4 py-2.5 rounded-xl border text-sm outline-none"
                  style={{
                    borderColor: SYSTEM_COLORS.border,
                    backgroundColor: SYSTEM_COLORS.shellSurface,
                    color: NEUTRAL_COLORS.black,
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="awarded">Awarded</option>
                </select>
              </div>

              {filteredCompetitions.length === 0 ? (
                <EmptyState
                  icon={Trophy}
                  title="No competitions found"
                  subtitle={
                    searchTerm || statusFilter !== 'all'
                      ? 'Try adjusting your search or filter.'
                      : 'Create the first competition to get started.'
                  }
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredCompetitions.map(comp => (
                    <CompetitionCard
                      key={comp.id}
                      competition={comp}
                      onEdit={() => { setSelectedCompetition(comp); setShowCompetitionModal(true); }}
                      onCreateApplication={() => { setSelectedCompetition(comp); setSelectedApplication(null); setShowApplicationModal(true); }}
                      canEdit={canEdit}
                      canCreateApplication={canManage}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Applications ── */}
          {activeTab === 'applications' && (
            <div className="p-5 sm:p-6">
              {applications.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No applications yet"
                  subtitle="Applications submitted by your team will appear here."
                />
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto rounded-xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <table className="min-w-full divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                      <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                        <tr>
                          {['Competition', 'Project', 'Status', 'Submitted', ''].map(h => (
                            <th
                              key={h}
                              className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                              style={{ color: SYSTEM_COLORS.textMuted }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ divideColor: SYSTEM_COLORS.borderSoft }}>
                        {applications.map(app => {
                          const comp = competitions.find(c => c.id === app.competitionId);
                          return (
                            <tr
                              key={app.id}
                              className="transition-colors"
                              style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = SYSTEM_COLORS.surfaceSubtle}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = SYSTEM_COLORS.shellSurface}
                            >
                              <td className="px-5 py-4">
                                <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                                  {comp?.name ?? 'Unknown'}
                                </p>
                                <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                                  {comp?.organization ?? '—'}
                                </p>
                              </td>
                              <td className="px-5 py-4 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                                {app.projectId ?? '—'}
                              </td>
                              <td className="px-5 py-4">
                                <span
                                  className="text-xs font-semibold px-2.5 py-1 rounded-full capitalize"
                                  style={appStatusStyle(app.status)}
                                >
                                  {app.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                                {app.submittedAt ? format(new Date(app.submittedAt), 'MMM d, yyyy') : 'Not submitted'}
                              </td>
                              <td className="px-5 py-4 text-right">
                                {canManage && (
                                  <button
                                    onClick={() => { setSelectedApplication(app); setShowApplicationModal(true); }}
                                    className="text-xs font-semibold transition-opacity hover:opacity-70"
                                    style={{ color: BRAND_COLORS.primary }}
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile card list */}
                  <div className="sm:hidden space-y-3">
                    {applications.map(app => {
                      const comp = competitions.find(c => c.id === app.competitionId);
                      return (
                        <div
                          key={app.id}
                          className="flex items-center gap-3 p-4 rounded-xl border"
                          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: NEUTRAL_COLORS.black }}>
                              {comp?.name ?? 'Unknown'}
                            </p>
                            <p className="text-xs mt-0.5 truncate" style={{ color: SYSTEM_COLORS.textMuted }}>
                              {comp?.organization ?? '—'}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <span
                                className="text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize"
                                style={appStatusStyle(app.status)}
                              >
                                {app.status.replace('_', ' ')}
                              </span>
                              <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                                {app.submittedAt ? format(new Date(app.submittedAt), 'MMM d') : 'Not submitted'}
                              </span>
                            </div>
                          </div>
                          {canManage && (
                            <button
                              onClick={() => { setSelectedApplication(app); setShowApplicationModal(true); }}
                              className="flex-shrink-0 p-1.5 rounded-lg hover:opacity-70 transition-opacity"
                              style={{ color: BRAND_COLORS.primary }}
                            >
                              <ChevronRight size={18} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showCompetitionModal && (
        <CompetitionModal
          isOpen={showCompetitionModal}
          onClose={handleModalClose}
          competition={selectedCompetition}
        />
      )}
      {showApplicationModal && (
        <ApplicationModal
          isOpen={showApplicationModal}
          onClose={handleModalClose}
          application={selectedApplication}
          competition={selectedCompetition}
        />
      )}
    </div>
  );
};

export default Competitions;
